// Lightweight canvas-based confetti for the Done page.
// Self-contained — no external deps. Fires once when shown.

import { useEffect, useRef } from "react";

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  rot: number;
  vr: number;
  life: number;
}

const COLORS = ["#0176d3", "#04844b", "#9050e9", "#f5a623", "#6fc4f5"];

export function Confetti(): JSX.Element {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const dpi = window.devicePixelRatio || 1;
    const resize = (): void => {
      canvas.width = window.innerWidth * dpi;
      canvas.height = window.innerHeight * dpi;
      ctx.scale(dpi, dpi);
    };
    resize();
    window.addEventListener("resize", resize);

    const particles: Particle[] = [];
    const burst = (cx: number, cy: number, count: number): void => {
      for (let i = 0; i < count; i += 1) {
        particles.push({
          x: cx,
          y: cy,
          vx: (Math.random() - 0.5) * 14,
          vy: -(Math.random() * 14 + 6),
          size: Math.random() * 6 + 4,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          rot: Math.random() * Math.PI * 2,
          vr: (Math.random() - 0.5) * 0.4,
          life: 0,
        });
      }
    };

    const w = window.innerWidth;
    const h = window.innerHeight;
    burst(w * 0.25, h * 0.5, 80);
    burst(w * 0.75, h * 0.5, 80);
    burst(w * 0.5, h * 0.4, 100);

    let raf = 0;
    const tick = (): void => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = particles.length - 1; i >= 0; i -= 1) {
        const p = particles[i];
        p.vy += 0.35;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vr;
        p.life += 1;
        if (p.life > 220 || p.y > window.innerHeight + 40) {
          particles.splice(i, 1);
          continue;
        }
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.5);
        ctx.restore();
      }
      if (particles.length > 0) {
        raf = requestAnimationFrame(tick);
      }
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 100,
      }}
    />
  );
}
